import React, { useEffect, useState, useRef, Component } from 'react';
import { Send, Search, X, Plus, MoreVertical, ArrowLeft, Paperclip, User, MessageSquare, AlertTriangle, Trash2, FileText, Download, Info } from 'lucide-react';
// Message type definition
type MessageType = 'text' | 'image' | 'file' | 'notification' | 'question' | 'program';
// Message interface
interface Message {
  id: string;
  content: string;
  sender: 'teacher' | 'student' | 'parent' | 'admin' | 'system';
  timestamp: Date;
  type: MessageType;
  attachments?: Array<{
    id: string;
    name: string;
    url: string;
    type: 'image' | 'file';
    size?: number;
  }>;
  questionData?: {
    title: string;
    subject: string;
    topic: string;
    content: string;
    image?: string;
    options: string[];
    correctAnswer?: number;
  };
  read?: boolean;
}
interface Conversation {
  id: string;
  title: string;
  recipient: {
    id: string;
    name: string;
    role: 'teacher' | 'student' | 'parent' | 'admin' | 'system';
  };
  lastMessage: string;
  timestamp: Date;
  unreadCount: number;
  status: 'active' | 'completed';
  messages: Message[];
}
function ConversationListItem({
  conversation,
  isActive,
  onClick
}: {
  conversation: Conversation;
  isActive: boolean;
  onClick: () => void;
}) {
  const formatTime = (date: Date) => {
    const now = new Date();
    const diffInDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    if (diffInDays === 0) {
      return date.toLocaleTimeString('tr-TR', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } else if (diffInDays === 1) {
      return 'Dün';
    } else if (diffInDays < 7) {
      return date.toLocaleDateString('tr-TR', {
        weekday: 'short'
      });
    } else {
      return date.toLocaleDateString('tr-TR', {
        day: '2-digit',
        month: '2-digit'
      });
    }
  };
  return <div onClick={onClick} className={`p-4 cursor-pointer transition-colors border-b border-emerald-900/20 last:border-b-0 ${isActive ? 'bg-emerald-500/10 border-l-2 border-l-emerald-500' : 'hover:bg-emerald-500/5'}`} data-id="element-1246">
      <div className="flex items-start space-x-3" data-id="element-1247">
        <div className="w-10 h-10 rounded-full bg-emerald-900/30 flex items-center justify-center flex-shrink-0" data-id="element-1248">
          <User className="w-5 h-5 text-emerald-400" data-id="element-1249" />
        </div>
        <div className="flex-1 min-w-0" data-id="element-1250">
          <div className="flex items-center justify-between mb-1" data-id="element-1251">
            <h3 className={`font-medium truncate ${isActive ? 'text-emerald-300' : 'text-white'}`} data-id="element-1252">
              {conversation.title}
            </h3>
            <div className="flex items-center space-x-1 flex-shrink-0 ml-2" data-id="element-1253">
              <span className="text-xs text-gray-400" data-id="element-1254">
                {formatTime(conversation.timestamp)}
              </span>
              {conversation.unreadCount > 0 && <span className="w-2 h-2 bg-emerald-500 rounded-full" data-id="element-1255"></span>}
            </div>
          </div>
          <div className="flex items-center justify-between" data-id="element-1256">
            <p className={`text-sm truncate ${conversation.unreadCount > 0 ? 'text-white font-medium' : 'text-gray-400'}`} data-id="element-1257">
              {conversation.lastMessage}
            </p>
          </div>
        </div>
      </div>
    </div>;
}
// Message Bubble Component
function MessageBubble({
  message
}: {
  message: Message;
}) {
  const isCurrentUser = message.sender === 'teacher';
  const isSystem = message.sender === 'system';
  // System messages have a special style
  if (isSystem) {
    return <div className="flex justify-center my-4" data-id="element-1258">
        <div className="px-4 py-2 bg-emerald-900/20 rounded-lg text-gray-400 text-sm max-w-[80%] border border-emerald-900/30" data-id="element-1259">
          {message.content}
        </div>
      </div>;
  }
  // Special rendering for question type
  if (message.type === 'question' && message.questionData) {
    return <div className={`flex ${isCurrentUser ? 'justify-end' : 'justify-start'} mb-4`} data-id="element-1260">
        <div className="relative max-w-[85%] bg-black border border-emerald-800/50 rounded-lg shadow-[0_0_10px_rgba(16,185,129,0.2)] p-4" data-id="element-1261">
          <div className="mb-3 border-b border-emerald-900/30 pb-2" data-id="element-1262">
            <h3 className="font-medium text-lg" data-id="element-1263">
              {message.questionData.title}
            </h3>
            <div className="flex items-center gap-2 mt-1" data-id="element-1264">
              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-900/30 text-emerald-400" data-id="element-1265">
                {message.questionData.subject}
              </span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-900/30 text-emerald-400" data-id="element-1266">
                {message.questionData.topic}
              </span>
            </div>
          </div>
          <div className="text-sm mb-3" data-id="element-1267">{message.questionData.content}</div>
          {message.questionData.image && <div className="mb-3 rounded-lg overflow-hidden" data-id="element-1268">
              <img src={message.questionData.image} alt="Soru görseli" className="w-full max-h-[200px] object-cover" data-id="element-1269" />
            </div>}
          <div className="space-y-2 mb-3" data-id="element-1270">
            {message.questionData.options.map((option, index) => <div key={index} className="flex items-center gap-2 p-2 rounded border border-emerald-900/30" data-id="element-1271">
                <span className="w-6 h-6 rounded-full bg-emerald-900/30 flex items-center justify-center text-xs text-emerald-400" data-id="element-1272">
                  {String.fromCharCode(65 + index)}
                </span>
                <span className="text-sm" data-id="element-1273">{option}</span>
              </div>)}
          </div>
          <div className="text-xs text-gray-400" data-id="element-1274">
            {message.timestamp.toLocaleTimeString('tr-TR', {
            hour: '2-digit',
            minute: '2-digit'
          })}
          </div>
        </div>
      </div>;
  }
  // Improved message bubble with better colors for incoming messages
  return <div className={`flex ${isCurrentUser ? 'justify-end' : 'justify-start'} mb-4`} data-id="element-1275">
      <div className={`relative max-w-[85%] px-4 py-3 rounded-lg ${isCurrentUser ? 'bg-gradient-to-r from-emerald-600 to-green-600 text-white' : 'bg-gradient-to-r from-slate-700 to-slate-600 text-white border border-emerald-900/40 shadow-[0_0_10px_rgba(16,185,129,0.1)]'}`} data-id="element-1276">
        {/* Gönderen bilgisi ekle */}
        {!isCurrentUser && <div className="text-xs text-emerald-300 mb-1 font-medium" data-id="element-1277">
            {message.sender === 'student' ? '👨‍🎓 Öğrenci' : '👨‍👩‍👧‍👦 Veli'}
          </div>}
        <div className="text-sm whitespace-pre-wrap" data-id="element-1278">{message.content}</div>
        {message.attachments && message.attachments.length > 0 && <div className="mt-2 space-y-2" data-id="element-1279">
            {message.attachments.map(attachment => <div key={attachment.id} className={`flex items-center gap-2 p-2 rounded border ${isCurrentUser ? 'border-emerald-300/30 bg-black/20' : 'border-emerald-500/30 bg-emerald-900/20'}`} data-id="element-1280">
                {attachment.type === 'image' ? <img src={attachment.url} alt={attachment.name} className="w-8 h-8 rounded object-cover" data-id="element-1281" /> : <FileText className="w-5 h-5 text-emerald-400" data-id="element-1282" />}
                <span className="text-xs" data-id="element-1283">{attachment.name}</span>
                <a href={attachment.url} download className="ml-auto" data-id="element-1284">
                  <Download className="w-4 h-4 text-emerald-400 hover:text-emerald-300" data-id="element-1285" />
                </a>
              </div>)}
          </div>}
        <div className={`text-xs mt-1 ${isCurrentUser ? 'opacity-70' : 'text-emerald-200/70'}`} data-id="element-1286">
          {message.timestamp.toLocaleTimeString('tr-TR', {
          hour: '2-digit',
          minute: '2-digit'
        })}
        </div>
      </div>
    </div>;
}
// Message Composer Component
function MessageComposer({
  onSendMessage,
  disabled = false
}: {
  onSendMessage: (content: string, attachments?: File[]) => void;
  disabled?: boolean;
}) {
  const [message, setMessage] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (message.trim() || attachments.length > 0) {
      onSendMessage(message.trim(), attachments);
      setMessage('');
      setAttachments([]);
    }
  };
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setAttachments(prev => [...prev, ...files]);
  };
  if (disabled) {
    return <div className="p-4 border-t border-emerald-900/30 bg-emerald-900/5" data-id="element-1287">
        <div className="text-center text-gray-400 text-sm" data-id="element-1288">
          Bu konuşma tamamlanmıştır
        </div>
      </div>;
  }
  return <form onSubmit={handleSubmit} className="p-4 border-t border-emerald-900/30" data-id="element-1289">
      {attachments.length > 0 && <div className="mb-3 flex flex-wrap gap-2" data-id="element-1290">
          {attachments.map((file, index) => <div key={index} className="flex items-center gap-2 px-3 py-1 bg-emerald-900/20 rounded-lg text-sm" data-id="element-1291">
              <Paperclip className="w-4 h-4" data-id="element-1292" />
              <span data-id="element-1293">{file.name}</span>
              <button type="button" onClick={() => setAttachments(prev => prev.filter((_, i) => i !== index))} className="text-gray-400 hover:text-white" data-id="element-1294">
                <X className="w-4 h-4" data-id="element-1295" />
              </button>
            </div>)}
        </div>}
      <div className="flex gap-2" data-id="element-1296">
        <button type="button" onClick={() => fileInputRef.current?.click()} className="p-2 text-gray-400 hover:text-emerald-400 transition-colors" data-id="element-1297">
          <Paperclip className="w-5 h-5" data-id="element-1298" />
        </button>
        <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleFileSelect} accept="image/*,.pdf,.doc,.docx" data-id="element-1299" />
        <input type="text" value={message} onChange={e => setMessage(e.target.value)} placeholder="Mesajınızı yazın..." className="flex-1 bg-black border border-emerald-900/30 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-1 focus:ring-emerald-500/50" data-id="element-1300" />
        <button type="submit" disabled={!message.trim() && attachments.length === 0} className="px-4 py-2 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all" data-id="element-1301">
          <Send className="w-5 h-5" data-id="element-1302" />
        </button>
      </div>
    </form>;
}
// Delete Confirmation Modal
function DeleteConfirmationModal({
  onClose,
  onConfirm
}: {
  onClose: () => void;
  onConfirm: () => void;
}) {
  return <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-id="element-1303">
      <div className="bg-black border border-emerald-900/50 rounded-lg p-6 max-w-md w-full mx-4" data-id="element-1304">
        <div className="flex items-center gap-3 mb-4" data-id="element-1305">
          <AlertTriangle className="w-6 h-6 text-red-400" data-id="element-1306" />
          <h3 className="text-lg font-semibold" data-id="element-1307">Sohbeti Sil</h3>
        </div>
        <p className="text-gray-400 mb-6" data-id="element-1308">
          Bu sohbeti silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
        </p>
        <div className="flex gap-3 justify-end" data-id="element-1309">
          <button onClick={onClose} className="px-4 py-2 border border-emerald-900/50 text-gray-300 rounded-lg hover:bg-emerald-500/5 transition-colors" data-id="element-1310">
            İptal
          </button>
          <button onClick={onConfirm} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors" data-id="element-1311">
            Sil
          </button>
        </div>
      </div>
    </div>;
}
// New Message Modal
function NewMessageModal({
  onClose,
  onCreateConversation,
  conversations
}: {
  onClose: () => void;
  onCreateConversation: (recipient: any, message: string) => void;
  conversations: Conversation[];
}) {
  const [selectedRecipient, setSelectedRecipient] = useState<any>(null);
  const [message, setMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  // Mock recipients - öğretmen paneli için öğrenciler ve veliler
  const recipients = [{
    id: '1',
    name: 'Ahmet Yılmaz',
    role: 'student',
    class: '8-A'
  }, {
    id: '2',
    name: 'Ayşe Demir',
    role: 'student',
    class: '8-A'
  }, {
    id: '3',
    name: 'Mehmet Kaya',
    role: 'student',
    class: '8-B'
  }, {
    id: '4',
    name: 'Fatma Yılmaz (Veli)',
    role: 'parent',
    child: 'Ahmet Yılmaz'
  }, {
    id: '5',
    name: 'Ali Demir (Veli)',
    role: 'parent',
    child: 'Ayşe Demir'
  }];
  const filteredRecipients = recipients.filter(recipient => recipient.name.toLowerCase().includes(searchQuery.toLowerCase()));
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedRecipient && message.trim()) {
      onCreateConversation(selectedRecipient, message.trim());
      onClose();
    }
  };
  return <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-id="element-1312">
      <div className="bg-black border border-emerald-900/50 rounded-lg p-6 max-w-md w-full mx-4" data-id="element-1313">
        <div className="flex items-center justify-between mb-4" data-id="element-1314">
          <h3 className="text-lg font-semibold" data-id="element-1315">Yeni Mesaj</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white" data-id="element-1316">
            <X className="w-6 h-6" data-id="element-1317" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4" data-id="element-1318">
          <div data-id="element-1319">
            <label className="block text-sm font-medium mb-2" data-id="element-1320">Alıcı Seç</label>
            <div className="relative mb-2" data-id="element-1321">
              <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Öğrenci veya veli ara..." className="w-full bg-black border border-emerald-900/30 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-emerald-500/50" data-id="element-1322" />
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-1323" />
            </div>
            <div className="max-h-32 overflow-y-auto border border-emerald-900/30 rounded-lg" data-id="element-1324">
              {filteredRecipients.map(recipient => <div key={recipient.id} onClick={() => {
              setSelectedRecipient(recipient);
            }} className={`p-3 cursor-pointer hover:bg-emerald-500/10 border-b border-emerald-900/20 last:border-b-0 transition-all duration-200 ${selectedRecipient?.id === recipient.id ? 'bg-emerald-500/20 border-l-4 border-l-emerald-500 transform scale-[1.02]' : 'hover:border-l-2 hover:border-l-emerald-500/50'}`} data-id="element-1325">
                  <div className="text-sm font-medium" data-id="element-1326">{recipient.name}</div>
                  <div className="text-xs text-gray-400" data-id="element-1327">
                    {recipient.role === 'student' ? `Sınıf: ${recipient.class}` : `Veli - ${recipient.child}`}
                  </div>
                </div>)}
            </div>
          </div>
          {/* Visual feedback for existing conversations */}
          {selectedRecipient && <div className="mb-3" data-id="element-1328">
              {conversations.find(conv => conv.recipient.id === selectedRecipient.id) ? <div className="flex items-center gap-2 p-2 bg-blue-900/20 border border-blue-500/30 rounded-lg" data-id="element-1329">
                  <Info className="w-4 h-4 text-blue-400" data-id="element-1330" />
                  <span className="text-xs text-blue-300" data-id="element-1331">
                    Bu kişiyle mevcut bir konuşmanız var. Mesaj oraya eklenecek.
                  </span>
                </div> : <div className="flex items-center gap-2 p-2 bg-emerald-900/20 border border-emerald-500/30 rounded-lg" data-id="element-1332">
                  <MessageSquare className="w-4 h-4 text-emerald-400" data-id="element-1333" />
                  <span className="text-xs text-emerald-300" data-id="element-1334">
                    Yeni konuşma başlatılacak.
                  </span>
                </div>}
            </div>}
          <div data-id="element-1335">
            <label className="block text-sm font-medium mb-2" data-id="element-1336">Mesaj</label>
            <textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="Mesajınızı yazın..." rows={4} className="w-full bg-black border border-emerald-900/30 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-emerald-500/50" data-id="element-1337" />
          </div>
          <div className="flex gap-3 justify-end" data-id="element-1338">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-emerald-900/50 text-gray-300 rounded-lg hover:bg-emerald-500/5 transition-colors" data-id="element-1339">
              İptal
            </button>
            <button type="submit" disabled={!selectedRecipient || !message.trim()} className="px-4 py-2 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all" data-id="element-1340">
              Gönder
            </button>
          </div>
        </form>
      </div>
    </div>;
}
// Main Messages Component
const Messaging = () => {
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [showNewMessageModal, setShowNewMessageModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const optionsMenuRef = useRef<HTMLDivElement>(null);
  // Replace useMessages hook with local state
  const [conversations, setConversations] = useState<Conversation[]>([{
    id: '1',
    title: 'Ahmet Yılmaz',
    recipient: {
      id: '1',
      name: 'Ahmet Yılmaz',
      role: 'student'
    },
    lastMessage: 'Yarınki sınav hakkında bilgi alabilir miyim?',
    timestamp: new Date(Date.now() - 30 * 60 * 1000),
    unreadCount: 2,
    status: 'active',
    messages: [{
      id: '1-1',
      content: 'Merhaba hocam, yarınki sınav hakkında bilgi alabilir miyim?',
      sender: 'student',
      timestamp: new Date(Date.now() - 35 * 60 * 1000),
      type: 'text'
    }, {
      id: '1-2',
      content: 'Merhaba Ahmet, yarınki sınav 2 saat sürecek ve tüm dönem konularını kapsayacak.',
      sender: 'teacher',
      timestamp: new Date(Date.now() - 34 * 60 * 1000),
      type: 'text'
    }, {
      id: '1-3',
      content: 'Hangi konulara ağırlık vermemiz gerekiyor?',
      sender: 'student',
      timestamp: new Date(Date.now() - 32 * 60 * 1000),
      type: 'text'
    }, {
      id: '1-4',
      content: 'Özellikle integral ve türev konularına ağırlık vermenizi öneririm. Son derste işlediğimiz problemlere benzer sorular gelecek.',
      sender: 'teacher',
      timestamp: new Date(Date.now() - 31 * 60 * 1000),
      type: 'text'
    }, {
      id: '1-5',
      content: 'Anladım, teşekkür ederim hocam. Bir de hesap makinesi kullanabilecek miyiz?',
      sender: 'student',
      timestamp: new Date(Date.now() - 30 * 60 * 1000),
      type: 'text'
    }]
  }, {
    id: '2',
    title: 'Ayşe Demir',
    recipient: {
      id: '2',
      name: 'Ayşe Demir',
      role: 'student'
    },
    lastMessage: 'Ödevleri kontrol ettiniz mi?',
    timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
    unreadCount: 0,
    status: 'active',
    messages: [{
      id: '2-1',
      content: 'Merhaba hocam, ödevleri kontrol ettiniz mi?',
      sender: 'student',
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
      type: 'text'
    }]
  }, {
    id: '3',
    title: 'Fatma Yılmaz (Veli)',
    recipient: {
      id: '4',
      name: 'Fatma Yılmaz (Veli)',
      role: 'parent'
    },
    lastMessage: 'Görüşme talebiniz için teşekkürler',
    timestamp: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
    unreadCount: 0,
    status: 'active',
    messages: [{
      id: '3-1',
      content: "Merhaba, Ahmet'in durumu hakkında görüşebilir miyiz?",
      sender: 'parent',
      timestamp: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000 - 30 * 60 * 1000),
      type: 'text'
    }, {
      id: '3-2',
      content: "Merhaba, tabii ki. Yarın saat 15:00'te uygun musunuz?",
      sender: 'teacher',
      timestamp: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
      type: 'text'
    }]
  }]);
  // Get active conversation
  const activeConversation = activeConversationId ? conversations.find(c => c.id === activeConversationId) : null;
  // Filter conversations based on search query
  const filteredConversations = conversations.filter(conversation => conversation.title.toLowerCase().includes(searchQuery.toLowerCase()) || conversation.recipient.name.toLowerCase().includes(searchQuery.toLowerCase()));
  // Updated functions to work with local state
  const markConversationAsRead = (conversationId: string) => {
    setConversations(conversations.map(conversation => conversation.id === conversationId ? {
      ...conversation,
      unreadCount: 0
    } : conversation));
  };
  const handleSendMessage = (content: string, attachments?: File[]) => {
    if (!activeConversationId || !activeConversation) return;
    const newMessage: Message = {
      id: `msg-${Date.now()}`,
      content,
      sender: 'teacher',
      timestamp: new Date(),
      type: 'text',
      attachments: attachments?.map((file, index) => ({
        id: `att-${Date.now()}-${index}`,
        name: file.name,
        url: URL.createObjectURL(file),
        type: file.type.startsWith('image/') ? 'image' : 'file',
        size: file.size
      })),
      read: true
    };
    // Mesajı conversation'a ekle ve state'i güncelle
    const updatedConversation = {
      ...activeConversation,
      messages: [...activeConversation.messages, newMessage],
      lastMessage: content,
      timestamp: new Date()
    };
    setConversations(prev => prev.map(conv => conv.id === activeConversationId ? updatedConversation : conv));
  };
  // Updated to check for existing conversations
  const handleCreateConversation = (recipient: any, message: string) => {
    // Önce mevcut konuşma var mı kontrol et
    const existingConversation = conversations.find(conv => conv.recipient.id === recipient.id);
    if (existingConversation) {
      // Mevcut konuşma varsa, ona mesaj ekle
      const newMessage: Message = {
        id: `msg-${Date.now()}`,
        content: message,
        sender: 'teacher',
        timestamp: new Date(),
        type: 'text',
        read: true
      };
      const updatedConversation = {
        ...existingConversation,
        messages: [...existingConversation.messages, newMessage],
        lastMessage: message,
        timestamp: new Date()
      };
      setConversations(prev => prev.map(conv => conv.id === existingConversation.id ? updatedConversation : conv));
      // Mevcut konuşmayı aç
      setActiveConversationId(existingConversation.id);
    } else {
      // Yeni konuşma oluştur
      const newConversation: Conversation = {
        id: `conv-${Date.now()}`,
        title: recipient.name,
        recipient,
        lastMessage: message,
        timestamp: new Date(),
        unreadCount: 0,
        status: 'active',
        messages: [{
          id: `msg-${Date.now()}`,
          content: message,
          sender: 'teacher',
          timestamp: new Date(),
          type: 'text',
          read: true
        }]
      };
      setConversations(prev => [newConversation, ...prev]);
      setActiveConversationId(newConversation.id);
    }
  };
  const handleDeleteConversation = () => {
    if (activeConversationId) {
      setConversations(prev => prev.filter(conv => conv.id !== activeConversationId));
      setActiveConversationId(null);
      setShowDeleteConfirmation(false);
    }
  };
  // Scroll to bottom of messages when conversation changes or new message is added
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({
        behavior: 'smooth'
      });
    }
  }, [activeConversation]);
  // Mark conversation as read when opened
  useEffect(() => {
    if (activeConversationId) {
      markConversationAsRead(activeConversationId);
    }
  }, [activeConversationId]);
  // Close options menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (optionsMenuRef.current && !optionsMenuRef.current.contains(event.target as Node)) {
        setShowOptionsMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const [showConversationList, setShowConversationList] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);
  return <div className="space-y-6" data-id="element-1341">
      <div className="flex items-center" data-id="element-1342">
        <MessageSquare size={24} className="mr-2 text-emerald-400" data-id="element-1343" />
        <h1 className="text-2xl font-bold" data-id="element-1344">Mesajlaşma</h1>
      </div>
      <div className="h-[calc(100vh-120px)] max-h-[800px] flex gap-6 bg-black" data-id="element-1345">
        {/* Conversation List - Hide on mobile when viewing a conversation */}
        {(!isMobile || isMobile && showConversationList) && <div className="w-full md:w-80 flex flex-col relative p-[1px] rounded-xl overflow-hidden bg-gradient-to-r from-emerald-600 to-green-600 shadow-[0_0_15px_rgba(16,185,129,0.3)]" data-id="element-1346">
            <div className="flex-1 flex flex-col bg-black rounded-xl overflow-hidden" data-id="element-1347">
              <div className="p-4 border-b border-emerald-900/30" data-id="element-1348">
                <div className="flex justify-between items-center mb-4" data-id="element-1349">
                  <h2 className="text-xl font-bold" data-id="element-1350">Mesajlar</h2>
                  <button onClick={() => setShowNewMessageModal(true)} className="p-2 rounded-full bg-black hover:bg-emerald-900/20 text-gray-400 hover:text-white transition-colors border border-emerald-900/50" data-id="element-1351">
                    <Plus className="w-5 h-5" data-id="element-1352" />
                  </button>
                </div>
                <div className="relative" data-id="element-1353">
                  <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Mesaj ara..." className="w-full bg-black border border-emerald-900/30 text-white rounded-lg pl-10 pr-4 py-2 focus:outline-none focus:ring-1 focus:ring-emerald-500/50" data-id="element-1354" />
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-1355" />
                  {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white" data-id="element-1356">
                      <X className="w-4 h-4" data-id="element-1357" />
                    </button>}
                </div>
              </div>
              <div className="flex-1 overflow-y-auto custom-scrollbar max-h-[600px]" data-id="element-1358">
                {filteredConversations.length > 0 ? filteredConversations.map(conversation => <ConversationListItem key={conversation.id} conversation={conversation} isActive={activeConversationId === conversation.id} onClick={() => {
              setActiveConversationId(conversation.id);
              if (isMobile) setShowConversationList(false);
            }} data-id="element-1359" />) : <div className="p-4 text-center text-gray-400" data-id="element-1360">
                    {searchQuery ? 'Mesaj bulunamadı' : 'Henüz mesaj yok'}
                  </div>}
              </div>
            </div>
          </div>}
        {/* Chat Area */}
        {(!isMobile || isMobile && !showConversationList) && <div className="flex-1 flex flex-col relative p-[1px] rounded-xl overflow-hidden bg-gradient-to-r from-emerald-600 to-green-600 shadow-[0_0_15px_rgba(16,185,129,0.3)]" data-id="element-1361">
            <div className="flex-1 flex flex-col bg-black rounded-xl overflow-hidden" data-id="element-1362">
              {activeConversation ? <>
                  <div className="p-4 border-b border-emerald-900/30 flex items-center justify-between" data-id="element-1363">
                    <div className="flex items-center gap-3" data-id="element-1364">
                      {isMobile && <button onClick={() => setShowConversationList(true)} className="text-gray-400 hover:text-white" data-id="element-1365">
                          <ArrowLeft className="w-5 h-5" data-id="element-1366" />
                        </button>}
                      <div className="w-10 h-10 rounded-full bg-emerald-900/30 flex items-center justify-center" data-id="element-1367">
                        <User className="w-5 h-5 text-emerald-400" data-id="element-1368" />
                      </div>
                      <div data-id="element-1369">
                        <h3 className="font-medium" data-id="element-1370">
                          {activeConversation.title}
                        </h3>
                        <p className="text-sm text-gray-400" data-id="element-1371">
                          {activeConversation.recipient.role === 'student' ? 'Öğrenci' : 'Veli'}
                        </p>
                      </div>
                    </div>
                    <div className="relative" ref={optionsMenuRef} data-id="element-1372">
                      <button onClick={() => setShowOptionsMenu(!showOptionsMenu)} className="p-2 text-gray-400 hover:text-white transition-colors" data-id="element-1373">
                        <MoreVertical className="w-5 h-5" data-id="element-1374" />
                      </button>
                      {showOptionsMenu && <div className="absolute right-0 top-full mt-2 w-48 bg-black border border-emerald-900/50 rounded-lg shadow-lg z-10" data-id="element-1375">
                          <button onClick={() => {
                    setShowDeleteConfirmation(true);
                    setShowOptionsMenu(false);
                  }} className="w-full px-4 py-2 text-left text-red-400 hover:bg-emerald-500/5 transition-colors flex items-center gap-2" data-id="element-1376">
                            <Trash2 className="w-4 h-4" data-id="element-1377" />
                            Sohbeti Sil
                          </button>
                        </div>}
                    </div>
                  </div>
                  <div className="flex-1 overflow-y-auto p-4 custom-scrollbar max-h-[500px]" data-id="element-1378">
                    {activeConversation.messages.map(message => <MessageBubble key={message.id} message={message} data-id="element-1379" />)}
                    <div ref={messagesEndRef} data-id="element-1380" />
                  </div>
                  <MessageComposer onSendMessage={handleSendMessage} disabled={activeConversation.status === 'completed'} data-id="element-1381" />
                </> : <div className="flex-1 flex flex-col items-center justify-center p-8 text-center" data-id="element-1382">
                  <div className="w-16 h-16 rounded-full bg-emerald-900/20 flex items-center justify-center mb-4" data-id="element-1383">
                    <MessageSquare className="w-8 h-8 text-emerald-400" data-id="element-1384" />
                  </div>
                  <h3 className="text-lg font-medium mb-2" data-id="element-1385">
                    Mesajlarınız burada görünecek
                  </h3>
                  <p className="text-gray-400 mb-6 max-w-md" data-id="element-1386">
                    Öğrenciler, veliler ve diğer öğretmenlerle iletişim kurmak
                    için bir konuşma başlatın.
                  </p>
                  <button onClick={() => setShowNewMessageModal(true)} className="px-4 py-2 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-lg hover:opacity-90 transition-colors" data-id="element-1387">
                    Yeni Mesaj
                  </button>
                </div>}
            </div>
          </div>}
        {showNewMessageModal && <NewMessageModal onClose={() => setShowNewMessageModal(false)} onCreateConversation={handleCreateConversation} conversations={conversations} data-id="element-1388" />}
        {showDeleteConfirmation && <DeleteConfirmationModal onClose={() => setShowDeleteConfirmation(false)} onConfirm={handleDeleteConversation} data-id="element-1389" />}
      </div>
    </div>;
};
export default Messaging;